import { Agents, Pools, Queues } from '@onlive.site/front-core';
import { createSlice } from '@reduxjs/toolkit';

// Firebase snapshot.val() returns null when the presence path is empty, and front-core's
// hasAgentOnline/hasAgentEnable getters crash with "Cannot convert undefined or null to object"
// (Object.values(null)). The Agents instance is a singleton shared through
// window.onliveManager.realtime.agents, so guarding _members here also protects the
// onlive-meet widget that reuses the same instance.
const guardAgentsMembers = (agents) => {
    if (!agents || Object.getOwnPropertyDescriptor(agents, '_members')?.get) return agents;
    let members = agents._members ?? {};
    Object.defineProperty(agents, '_members', {
        configurable: true,
        get: () => members,
        set: (value) => {
            members = value ?? {};
        },
    });
    return agents;
};

export const realtimeSlice = createSlice({
    name: 'realtime',
    initialState: {
        queues: null,
        agents: null,
        pools: null,
        meetLoaded: false,
        realtimeLoaded: false,
        sessionRequest: null,
        available: false,
        forcedOffline: false,
        // Firebase has answered once about this agent's node: until then `available` and
        // `forcedOffline` are only defaults, not what the agent chose.
        presenceKnown: false,
        iChat: null,
        iChatRequests: [],
        pool: [],
        queue: [],
    },
    reducers: {
        initRealTimes: (state, action) => {
            state.realtimeLoaded = true;
            let realtimeAgents = guardAgentsMembers(
                Agents.getInstance(action.payload.organizationId, 'firebase', action.payload.firebase),
            );
            let realtimeQueues = Queues.getInstance(action.payload.organizationId, 'firebase', action.payload.firebase);
            let realtimePools = Pools.getInstance(action.payload.organizationId, 'firebase', action.payload.firebase);
            state.queues = realtimeQueues;
            state.agents = realtimeAgents;
            state.pools = realtimePools;

            // Store Firebase config and organizationId in sessionStorage for external scripts
            const vmsState = {
                state: {
                    firebaseConfig: action.payload.firebase,
                    organizationId: action.payload.organizationId,
                    userId: action.payload.userId,
                    userGroupId: action.payload.userGroupId,
                    managedGroups: action.payload.managedGroups,
                },
            };
            sessionStorage.setItem('vms-mf-state', JSON.stringify(vmsState));
        },
        setAgentAvailable: (state, action) => {
            state.available = action.payload;
        },
        setAgentForcedOffline: (state, action) => {
            state.forcedOffline = !!action.payload;
        },
        setAgentPresence: (state, action) => {
            state.available = !!action.payload?.available;
            state.forcedOffline = !!action.payload?.forcedOffline;
            state.presenceKnown = true;
        },
        setMeetLoaded: (state, action) => {
            state.meetLoaded = action.payload;
        },
        setPool: (state, action) => {
            state.pool = action.payload;
        },
        setQueue: (state, action) => {
            state.queue = action.payload;
        },
        setIChat: (state, action) => {
            state.iChat = action.payload;
        },
        setSessionRequest: (state, action) => {
            state.request = action.payload;
        },
        setIchatRequests: (state, action) => {
            if (state.iChatRequests.length < action.payload.length)
                window.dispatchEvent(new CustomEvent('onlive-ichat-notification'));
            state.iChatRequests = action.payload;
        },
        resetRealtime: (state) => {
            state.queues = null;
            state.agents = null;
            state.pools = null;
            state.meetLoaded = false;
            state.realtimeLoaded = false;
            state.sessionRequest = null;
            state.available = false;
            state.forcedOffline = false;
            state.presenceKnown = false;
            state.iChat = null;
            state.iChatRequests = [];
            state.pool = [];
            state.queue = [];
        },
    },
});

export const {
    initRealTimes,
    setMeetLoaded,
    setSessionRequest,
    setAgentAvailable,
    setAgentForcedOffline,
    setAgentPresence,
    setIChat,
    setIchatRequests,
    setPool,
    setQueue,
    resetRealtime,
} = realtimeSlice.actions;

export const getRealtimeAgents = (state) => state.realtime.agents;
export const getRealtimeQueues = (state) => state.realtime.queues;
export const getRealtimePools = (state) => state.realtime.pools;
export const getMeetLoaded = (state) => state.realtime.meetLoaded;
export const getRealtimeLoaded = (state) => state.realtime.realtimeLoaded && state.realtime.meetLoaded;
export const getSessionRequest = (state) => state.realtime.request;
export const getAgentAvailable = (state) => state.realtime.available;
export const getAgentForcedOffline = (state) => state.realtime.forcedOffline;
export const getAgentPresenceKnown = (state) => state.realtime.presenceKnown;
export const getIChat = (state) => state.realtime.iChat;
export const getIchatRequests = (state) => state.realtime.iChatRequests;
export const getPool = (state) => state.realtime.pool;
export const getQueue = (state) => state.realtime.queue;

export default realtimeSlice.reducer;

export const loadIchat = (user, ichatVersion, context) => (dispatch) => {
    if (document.body.querySelector('#onlive-ichat')) return;
    const script = document.createElement('script');
    script.id = 'onlive-ichat';
    let basePath = process.env.REACT_APP_ICHAT_URL;
    let bundleName = process.env.REACT_APP_ENVIRONMENT === 'production' ? 'admin.min.js' : 'admin.js';

    script.src =
        process.env.REACT_APP_ENVIRONMENT === 'production'
            ? `${basePath}/${ichatVersion}/${bundleName}`
            : `${basePath}`;

    script.async = true;
    script.onload = () => {
        const agentIchatEl = document.createElement('agent-ichat');
        agentIchatEl.organizationId = user.organizationId;
        agentIchatEl.nickname = user.firstName;
        agentIchatEl.avatar = user.avatar;
        agentIchatEl.showBubble = false;
        agentIchatEl.context = context;
        dispatch(setIChat(agentIchatEl));
    };
    window.addEventListener('onlive-ichat-request', (e) => {
        dispatch(setIchatRequests(e.detail || []));
    });
    document.body.appendChild(script);
};
